import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, LinearProgress } from "@mui/material";
import {
  uploadAtlFile,
  stopAtlUpload,
  cancelAtlUpload,
  clearAtlUploadResult,
} from "../../Actions/AtlAction";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import {
  formatBytes,
  formatCount,
  formatDateTime,
  formatDuration,
  PHASE_LABELS,
  STATUS_STYLES,
} from "./format";

const ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];

// The file is far too large to open in the browser the way the bulk invoice
// uploader does (XLSX.read on 300 MB would freeze the tab). An .xlsx is a zip,
// so its name and first four bytes are enough to catch a wrong file here; the
// server checks the contents properly.
const looksLikeXlsx = async (file) => {
  if (!/\.xlsx$/i.test(file.name)) return false;
  try {
    const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    return ZIP_SIGNATURE.every((byte, index) => head[index] === byte);
  } catch (error) {
    // Could not read it here; let the server decide.
    return true;
  }
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.cancelled;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${style.className}`}
    >
      {style.label}
    </span>
  );
};

// The import whose result has already been announced with an alert. Kept
// outside the component so coming back to this screen does not announce the
// same result a second time.
let announcedImportId = "";

const AtlUpload = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const {
    dataset,
    activeImport,
    lastImport,
    atlHistory,
    isGettingAtlHistory,
    hasLoadedAtlStatus,
    isCancellingAtlUpload,
    uploadStatus,
    uploadFileName,
    uploadLoaded,
    uploadTotal,
    uploadResumed,
    uploadError,
    uploadImportId,
  } = useSelector((state) => state.AtlReducer);

  const isUploading = uploadStatus === "uploading";
  const isProcessing = activeImport?.status === "processing";
  // An upload the server knows about that this browser is not sending: one
  // left unfinished earlier, or another admin's.
  const waitingUpload =
    !isUploading && activeImport?.status === "uploading" ? activeImport : null;
  const busy = isUploading || isProcessing || Boolean(waitingUpload);
  const uploadPercent = uploadTotal
    ? Math.min(100, Math.floor((uploadLoaded / uploadTotal) * 100))
    : 0;

  // Tell the admin once when the import they started has finished.
  useEffect(() => {
    if (!uploadImportId || !lastImport || lastImport.id !== uploadImportId) return;
    if (announcedImportId === uploadImportId) return;
    if (lastImport.status === "completed") {
      announcedImportId = uploadImportId;
      handleVisible(
        `The taxpayer list has been updated: ${formatCount(lastImport.totalRows)} records.`,
      );
    } else if (lastImport.status === "failed") {
      announcedImportId = uploadImportId;
      handleDangerVisible(lastImport.error || "The import failed.");
    }
  }, [uploadImportId, lastImport]);

  const handleFileChange = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    dispatch(clearAtlUploadResult());
    if (!(await looksLikeXlsx(picked))) {
      setFile(null);
      setFileError(
        "Only the Excel file (.xlsx) downloaded from FBR can be uploaded.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(picked);
    setFileError("");
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = () => {
    if (!file) {
      setFileError("Please choose the Excel file to upload.");
      return;
    }
    dispatch(uploadAtlFile(file));
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCancelWaiting = () => {
    dispatch(
      cancelAtlUpload(waitingUpload.id, handleVisible, handleDangerVisible),
    );
  };

  return (
    <div className="space-y-6">
      {/* What is live right now */}
      <div>
        <h2
          className="font-bold text-lg m-0"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Current List
        </h2>
        {!hasLoadedAtlStatus ? (
          <div className="flex items-center h-12">
            <CircularProgress size={22} />
          </div>
        ) : dataset ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3 text-sm">
            <div>
              <p className="text-slate-500 m-0">Active taxpayers</p>
              <p className="text-xl font-bold m-0">
                {formatCount(dataset.totalRows)}
              </p>
            </div>
            <div>
              <p className="text-slate-500 m-0">Last updated</p>
              <p className="font-semibold m-0">
                {formatDateTime(dataset.importedAt)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-slate-500 m-0">File</p>
              <p className="font-semibold m-0 truncate" title={dataset.fileName}>
                {dataset.fileName || "-"}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 mt-2 mb-0">
            No list has been uploaded yet.
          </p>
        )}
      </div>

      {/* Upload */}
      <div className="space-y-4">
        <h2
          className="font-bold text-lg m-0"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Upload Active Taxpayer List
        </h2>

        {/* This browser is sending the file */}
        {isUploading && (
          <div
            className="border border-blue-200 bg-blue-50 rounded-lg p-4"
            role="status"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="font-semibold text-blue-900 m-0 truncate">
                Uploading {uploadFileName}
              </p>
              <button
                type="button"
                onClick={() => dispatch(stopAtlUpload())}
                className="px-3 py-1 rounded-md border border-red-300 text-red-600 text-sm font-semibold bg-white"
              >
                Cancel Upload
              </button>
            </div>
            <LinearProgress
              variant="determinate"
              value={uploadPercent}
              sx={{ my: 1.5, height: 8, borderRadius: 4 }}
            />
            <p className="text-sm text-blue-900 m-0">
              {uploadPercent}% · {formatBytes(uploadLoaded)} of{" "}
              {formatBytes(uploadTotal)}
              {uploadResumed ? " · continued from an earlier attempt" : ""}
            </p>
            <p className="text-xs text-blue-800 mt-1 mb-0">
              Keep this browser tab open until the upload reaches 100%. If the
              connection drops it continues by itself from where it stopped.
            </p>
          </div>
        )}

        {/* The server is building the new list */}
        {isProcessing && (
          <div
            className="border border-blue-200 bg-blue-50 rounded-lg p-4"
            role="status"
          >
            <p className="font-semibold text-blue-900 m-0">
              Processing {activeImport.fileName}:{" "}
              {PHASE_LABELS[activeImport.phase] || "Starting"}...
            </p>
            <LinearProgress
              variant="determinate"
              value={activeImport.percent || 0}
              sx={{ my: 1.5, height: 8, borderRadius: 4 }}
            />
            <p className="text-sm text-blue-900 m-0">
              {activeImport.percent || 0}%
              {activeImport.sheetsTotal
                ? ` · sheet ${activeImport.sheetsDone} of ${activeImport.sheetsTotal}`
                : ""}
              {activeImport.rowsProcessed
                ? ` · ${formatCount(activeImport.rowsProcessed)} rows read`
                : ""}
            </p>
            <p className="text-xs text-blue-800 mt-1 mb-0">
              This runs on the server - you can leave this page. The current
              list stays available for searching until the new one is ready.
            </p>
          </div>
        )}

        {/* An upload exists on the server that this browser is not sending */}
        {waitingUpload && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
            <p className="font-semibold text-amber-900 m-0">
              An upload of {waitingUpload.fileName} is unfinished (
              {waitingUpload.percent || 0}% received
              {waitingUpload.uploadedBy ? `, started by ${waitingUpload.uploadedBy}` : ""}
              ).
            </p>
            <p className="text-sm text-amber-900 mt-1 mb-3">
              If it is yours, choose the same file again to continue it. If
              someone else is uploading right now, wait for it to finish.
              Otherwise cancel it to upload a different file.
            </p>
            <button
              type="button"
              disabled={isCancellingAtlUpload}
              onClick={handleCancelWaiting}
              className="px-3 py-1 rounded-md border border-red-300 text-red-600 text-sm font-semibold bg-white disabled:opacity-50"
            >
              {isCancellingAtlUpload ? "Please Wait..." : "Cancel That Upload"}
            </button>
          </div>
        )}

        {/* The last attempt from this browser failed before it was complete */}
        {uploadStatus === "error" && (
          <div
            className="border border-red-200 bg-red-50 rounded-lg p-4"
            role="alert"
          >
            <p className="font-semibold text-red-700 m-0">Upload failed</p>
            <p className="text-sm text-red-700 mt-1 mb-0">{uploadError}</p>
            <p className="text-xs text-red-700 mt-1 mb-0">
              The current list has not been changed.
            </p>
          </div>
        )}

        {/* How the most recent import ended */}
        {!busy && lastImport?.status === "failed" && (
          <div
            className="border border-red-200 bg-red-50 rounded-lg p-4"
            role="alert"
          >
            <p className="font-semibold text-red-700 m-0">
              The last import failed ({lastImport.fileName},{" "}
              {formatDateTime(lastImport.finishedAt || lastImport.createdAt)})
            </p>
            <p className="text-sm text-red-700 mt-1 mb-0">{lastImport.error}</p>
          </div>
        )}
        {!busy && lastImport?.status === "completed" && (
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <p className="font-semibold text-green-800 m-0">
              Last import completed: {formatCount(lastImport.totalRows)} records
              from {lastImport.fileName} in {formatDuration(lastImport.durationMs)}
            </p>
          </div>
        )}

        {/* File picker */}
        <div>
          <label htmlFor="atl-file" className="block mb-1 font-medium">
            Excel File (.xlsx)<span className="text-red-500">*</span>
          </label>
          {!file ? (
            <input
              id="atl-file"
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              disabled={isUploading || isProcessing}
              onChange={handleFileChange}
              className={`w-full px-3 py-2 border rounded-md disabled:opacity-50 ${
                fileError ? "border-red-500" : "border-gray-300"
              }`}
            />
          ) : (
            <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-gray-50">
              <span className="text-sm truncate">
                {file.name} ({formatBytes(file.size)})
              </span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-sm text-red-600 font-semibold ml-3"
              >
                Remove
              </button>
            </div>
          )}
          {fileError ? (
            <p className="text-red-500 text-sm mt-1">{fileError}</p>
          ) : (
            <p className="text-gray-500 text-sm mt-1">
              The Active Taxpayer List exactly as downloaded from FBR. Large
              files (300 MB and more) are supported.
            </p>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-4">
          <h6 className="font-semibold text-sm mb-2">How this works</h6>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>The new file replaces the whole list; it is not added to it</li>
            <li>
              The current list keeps working while the new one is uploaded and
              processed
            </li>
            <li>
              If the upload or the import fails, the current list is left
              unchanged and the reason is shown here
            </li>
            <li>
              Do not open and re-save the file in Excel before uploading it
            </li>
          </ul>
        </div>

        <button
          type="button"
          disabled={isUploading || isProcessing || !file}
          onClick={handleUpload}
          className="w-full md:w-1/3 block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
          style={{ background: globalStyle.buttonColor }}
        >
          {isUploading
            ? "Uploading..."
            : isProcessing
              ? "Processing..."
              : "Upload & Replace List"}
        </button>
      </div>

      {/* History */}
      <div>
        <h2
          className="font-bold text-lg m-0 mb-3"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Recent Uploads
        </h2>
        {isGettingAtlHistory && !atlHistory.length ? (
          <div className="flex justify-center items-center h-16">
            <CircularProgress size={24} />
          </div>
        ) : !atlHistory.length ? (
          <p className="text-sm text-slate-500 m-0">Nothing uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="text-slate-500 border-b">
                  <th className="py-2 pr-4 font-semibold">Date</th>
                  <th className="py-2 pr-4 font-semibold">File</th>
                  <th className="py-2 pr-4 font-semibold">Size</th>
                  <th className="py-2 pr-4 font-semibold">Records</th>
                  <th className="py-2 pr-4 font-semibold">Time Taken</th>
                  <th className="py-2 pr-4 font-semibold">Uploaded By</th>
                  <th className="py-2 pr-4 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {atlHistory.map((job) => (
                  <tr key={job.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {formatDateTime(job.createdAt)}
                    </td>
                    <td className="py-2 pr-4 break-all">{job.fileName}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {formatBytes(job.fileSize)}
                    </td>
                    <td className="py-2 pr-4">
                      {job.status === "completed"
                        ? formatCount(job.totalRows)
                        : "-"}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {job.status === "completed"
                        ? formatDuration(job.durationMs)
                        : "-"}
                    </td>
                    <td className="py-2 pr-4">{job.uploadedBy || "-"}</td>
                    <td className="py-2 pr-4">
                      <StatusBadge status={job.status} />
                      {job.error && job.status !== "completed" && (
                        <p className="text-xs text-slate-500 mt-1 mb-0 max-w-md">
                          {job.error}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AtlUpload;
